import { useMemo, useState, useSyncExternalStore } from 'react';
import { Bell, ChevronLeft, Lock, Users } from '../../../icons';
import { Button, DocTitleInput, FilterChip } from '../../../components/shared';
import { AttachmentUploader } from '../../../components/Attachment/AttachmentUploader';
import { boardQuotaNoticeKey } from '../boardQuotaNotice';
import { useAttachmentUpload } from '../../../components/Attachment/useAttachmentUpload';
import {
  createBoardPost, patchBoardPost,
  type BoardDto, type BoardPostDetail, type BoardPrefixDto,
} from '../../../api/boards';
import type { TargetRow } from '../TargetPicker';
import { BoardTargetModal } from './BoardTargetModal';
import { BoardSwitch } from './BoardSwitch';
import { tBoards, getBoardsLang, subscribeBoardsLang } from '../../../lib/boards-i18n';

/**
 * 글쓰기 — 본문은 넓게, 설정은 오른쪽 (시안 03, 2026-08-18).
 *
 * 종전 `BoardPostEditor` 는 본문 아래에 체크박스가 줄줄이 붙고 열람자·알림 대상이 그 밑으로
 * 더 길어졌다. 여기서는 **쓰는 것과 보내는 것을 좌우로 가른다**.
 *
 * 보내는 값은 종전과 **한 글자도 다르지 않다** — `createBoardPost` payload 동일
 * (allowComments 는 게시판 기본값과 같으면 null 로 보내 서버 기본을 따르게 하는 것까지 같다).
 *
 * 게시판 설정에서 꺼진 항목은 **아예 그리지 않는다**(서버도 400 으로 막는다):
 *   익명 = allowAnonymous · 열람자 = usePostReaders · 알림 = notifyEnabled.
 *
 * ⚠ 기존 `BoardPostEditor` · `TargetPicker` 는 한 줄도 건드리지 않는다.
 *   플래그(VITE_BOARD_WIDE=0)를 끄면 종전 화면·종전 글쓰기가 그대로 돌아온다.
 *
 * ## 수정 모드 (2026-08-19)
 *
 * `initial` 을 주면 **같은 화면이 수정 페이지가 된다.** 폼을 한 벌 더 만들지 않는 이유는
 * 분명하다 — 글쓰기와 수정은 같은 항목을 같은 규칙으로 다루고, 두 벌이면 한쪽에만 항목이
 * 붙는 어긋남이 반드시 생긴다(익명·비공개·열람자·첨부가 전부 그렇다).
 *
 * 수정에서 다른 것은 셋뿐이다:
 *   · 저장이 `patchBoardPost` 로 간다(서버가 준 값만 바꾼다).
 *   · **알림 재발송은 여기서 하지 않는다** — 고칠 때마다 전사에 다시 울리면 안 된다.
 *     재발송은 별도 동작(POST /posts/:id/notify)이고 화면도 따로다.
 *   · 첨부는 **기존 것을 이어받는다**. 편집 중 지우면 저장 시 서버가 회수한다.
 */

const TITLE_MAX = 300;   // 서버 POST_TITLE_MAX
const CONTENT_MAX = 10000; // 서버 POST_CONTENT_MAX

type PickerKind = 'notify' | 'readers' | null;

/** 담긴 대상을 종류별로 요약한다 — 이름 해석은 고르는 창(TargetPicker)에 맡긴다. */
export function summarizeTargets(rows: TargetRow[]): string {
  if (rows.length === 0) return tBoards('boards.target.empty');
  const order: Array<TargetRow['targetType']> = ['company', 'division', 'team', 'user'];
  const label: Record<TargetRow['targetType'], string> = {
    company: tBoards('boards.target.company'),
    division: tBoards('boards.target.division'),
    team: tBoards('boards.target.team'),
    user: tBoards('boards.target.user'),
  };
  return order
    .map((t) => ({ t, n: rows.filter((r) => r.targetType === t).length }))
    .filter((x) => x.n > 0)
    .map((x) => (x.t === 'company' ? label.company : `${label[x.t]} ${x.n}`))
    .join(' · ');
}

interface BoardWriterProps {
  board: BoardDto;
  prefixes: BoardPrefixDto[];
  onCancel: () => void;
  onCreated: (post: BoardPostDetail, notified: number) => void;
  /**
   * 주면 **수정 모드**. 이 글의 값으로 폼을 채우고 저장은 PATCH 로 간다 (2026-08-19).
   * 안 주면 지금까지와 완전히 같은 글쓰기다.
   */
  initial?: BoardPostDetail | null;
}

export function BoardWriter({ board, prefixes, onCancel, onCreated, initial }: BoardWriterProps) {
  useSyncExternalStore(subscribeBoardsLang, getBoardsLang, getBoardsLang);
  const editing = !!initial;
  const [title, setTitle] = useState(initial?.title ?? '');
  const [content, setContent] = useState(initial?.content ?? '');
  const [prefixId, setPrefixId] = useState(initial?.prefix?.id ?? '');
  const [isAnonymous, setIsAnonymous] = useState(initial?.isAnonymous ?? false);
  const [isSecret, setIsSecret] = useState(initial?.isSecret ?? false);
  const [allowComments, setAllowComments] = useState(initial?.commentsAllowed ?? board.allowComments);
  const [readers, setReaders] = useState<TargetRow[]>(
    () => (initial?.readers ?? []).map((r) => ({ targetType: r.targetType, targetId: r.targetId })),
  );
  // 수정에서는 알림을 기본으로 끈다 — 고칠 때마다 전사에 다시 울리면 안 된다.
  const [notify, setNotify] = useState(!initial && board.notifyEnabled && board.notifyAllByDefault);
  const [notifyTargets, setNotifyTargets] = useState<TargetRow[]>([]);
  const [picker, setPicker] = useState<PickerKind>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 게시판 첨부의 용량은 그 게시판의 소유 스코프에서 나간다 (2026-08-19).
  // boardId 를 실어 보내야 서버가 회사·팀·개인 중 어디에서 뺄지 안다.
  // 수정이면 이미 붙어 있던 첨부를 씨앗으로 받는다(빼면 저장 때 서버가 회수한다).
  const upload = useAttachmentUpload({
    onError: setError,
    boardId: board.id,
    initialAttachments: useMemo(
      () => (initial?.attachments ?? []).map((a) => ({
        fileId: a.fileId,
        fileKey: '',
        name: a.name,
        mimeType: a.mimeType ?? '',
        sizeBytes: a.sizeBytes ?? 0,
      })),
      // 첫 렌더에만 쓰이는 씨앗이라 글이 바뀌지 않는 한 다시 만들 필요가 없다.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [initial?.id],
    ),
  });

  const canSubmit = title.trim().length > 0 && content.trim().length > 0 && !submitting;

  async function submit() {
    if (!title.trim() || !content.trim()) {
      setError(tBoards('boards.editor.err_required'));
      return;
    }
    setSubmitting(true);
    setError(null);
    // 새 글과 수정이 **같은 값**을 보낸다 — 두 벌로 쓰면 한쪽에만 항목이 붙는다.
    const common = {
      title: title.trim(),
      content: content.trim(),
      contentType: 'TEXT' as const,
      prefixId: prefixId || null,
      isAnonymous,
      isSecret,
      allowComments: allowComments === board.allowComments ? null : allowComments,
      attachments: upload.attachments.map((a) => ({
        fileId: a.fileId,
        name: a.name,
        sizeBytes: a.sizeBytes,
        ...(a.mimeType ? { mimeType: a.mimeType } : {}),
      })),
      readers: board.usePostReaders
        ? readers.map((r) => ({ targetType: r.targetType, targetId: r.targetId ?? '0' }))
        : undefined,
    };
    try {
      if (initial) {
        // ⚠ 수정에는 notify 를 싣지 않는다 — 고칠 때마다 전사에 다시 울리면 안 된다.
        //   재발송은 별도 동작(POST /posts/:id/notify)이다.
        const post = await patchBoardPost(initial.id, common);
        upload.clearAttachments();
        onCreated(post, 0);
        return;
      }
      const { post, notified } = await createBoardPost(board.id, {
        ...common,
        notify: board.notifyEnabled
          ? {
              enabled: notify,
              targets: notifyTargets.map((r) => ({ targetType: r.targetType, targetId: r.targetId ?? '0', canNotify: true })),
            }
          : undefined,
      });
      upload.clearAttachments();
      onCreated(post, notified);
    } catch (e: any) {
      setError(tBoards(initial ? 'boards.editor.err_update' : 'boards.editor.err_create', {
        msg: e?.response?.data?.message ?? e?.message ?? 'unknown',
      }));
    } finally {
      setSubmitting(false);
    }
  }

  const notifySummary = useMemo(() => summarizeTargets(notifyTargets), [notifyTargets]);
  const readersSummary = useMemo(() => summarizeTargets(readers), [readers]);

  return (
    <section className="bdw-writer" aria-label={tBoards(editing ? 'boards.editor.heading_edit' : 'boards.editor.heading')}>
      <div className="bdw-wbar">
        <Button variant="secondary"
          onClick={onCancel} disabled={submitting}
          iconLeft={<ChevronLeft size={14} aria-hidden />}
        >
          {board.name}
        </Button>
        <span className="bdw-rbar-sep" aria-hidden />
        <h2 className="bdw-wbar-title">
          {tBoards(editing ? 'boards.editor.heading_edit' : 'boards.editor.heading')}
        </h2>

        <span className="bdw-rbar-right">
          <Button variant="secondary"
            onClick={onCancel} disabled={submitting}
          >
            {tBoards('boards.btn.cancel')}
          </Button>
          <Button variant="primary"
            onClick={submit} disabled={!canSubmit}
          >
            {tBoards(editing ? 'boards.btn.save' : 'boards.btn.register')}
          </Button>
        </span>
      </div>

      <div className="bdw-wbody">
        <div className="bdw-wmain">
          {prefixes.length > 0 && (
            <div className="bdw-wprefix" role="group" aria-label={tBoards('boards.editor.prefix')}>
              <span className="bdw-wprefix-lab">{tBoards('boards.editor.prefix')}</span>
              <FilterChip pressed={prefixId === ''} onClick={() => setPrefixId('')}>
                {tBoards('boards.editor.prefix_none')}
              </FilterChip>
              {prefixes.map((p) => (
                <FilterChip key={p.id} pressed={prefixId === p.id} onClick={() => setPrefixId(p.id)}>
                  {p.label}
                </FilterChip>
              ))}
            </div>
          )}

          {/*
            정본 B15 「문서 제목 칸」 (2026-09-07) — 결재 기안(`.apn__ttl`)과 **같은 물건**이다.
            ⚠ 본문(`.bdw-wtext`)은 화면 높이를 다 쓰는 «편집 바닥»이라 정본 `Textarea`
              (최소 높이 h-lg×2)와 다른 물건이다 — 그대로 둔다.
          */}
          <DocTitleInput
            wrapperClassName="bdw-wtitle"
            value={title}
            maxLength={TITLE_MAX}
            placeholder={tBoards('boards.editor.title')}
            aria-label={tBoards('boards.editor.title')}
            onChange={(e) => setTitle(e.target.value)}
          />

          <div className="bdw-wcontent">
            <textarea
              className="bdw-wtext"
              value={content}
              maxLength={CONTENT_MAX}
              placeholder={tBoards('boards.editor.content')}
              aria-label={tBoards('boards.editor.content')}
              onChange={(e) => setContent(e.target.value)}
            />
            <span className="bdw-wcount bd-num">{content.length} / {CONTENT_MAX.toLocaleString()}</span>
          </div>

          <div className="bdw-watt">
            <AttachmentUploader state={upload} disabled={submitting} />
            <p className="bdw-watt-quota">{tBoards(boardQuotaNoticeKey(board.scopeType))}</p>
          </div>

          {error && <p className="bd-error" role="alert">{error}</p>}
        </div>

        <aside className="bdw-wside">
          <section className="bdw-wbox">
            <h3>{tBoards('boards.wide.publish')}</h3>

            {board.allowAnonymous && (
              <BoardSwitch
                label={tBoards('boards.editor.anonymous')}
                checked={isAnonymous}
                onChange={setIsAnonymous}
              />
            )}
            <BoardSwitch
              label={tBoards('boards.editor.secret')}
              hint={board.usePostReaders ? tBoards('boards.wide.secret_hint') : undefined}
              checked={isSecret}
              onChange={setIsSecret}
            />
            <BoardSwitch
              label={tBoards('boards.editor.allow_comments')}
              checked={allowComments}
              onChange={setAllowComments}
            />
            {/* 수정에서는 알림 항목을 아예 그리지 않는다 — 보내지 않는 값을 켤 수 있게
                두면 "켰는데 안 갔다" 가 된다. 재발송은 별도 동작이다. */}
            {!editing && board.notifyEnabled && (
              <BoardSwitch
                label={tBoards('boards.editor.notify')}
                checked={notify}
                onChange={setNotify}
              />
            )}
          </section>

          {!editing && board.notifyEnabled && (
            <section className={`bdw-wbox${notify ? '' : ' bdw-wbox--locked'}`}>
              <h3>{tBoards('boards.editor.notify_targets')}</h3>
              {notify ? (
                <>
                  <p className="bdw-whint">{tBoards('boards.editor.notify_default')}</p>
                  <p className="bdw-wsummary">
                    <Bell size={13} aria-hidden /> {notifySummary}
                  </p>
                  <Button variant="secondary"
                    onClick={() => setPicker('notify')}
                    iconLeft={<Users size={14} aria-hidden />}
                  >
                    {tBoards('boards.wide.pick_targets')}
                  </Button>
                </>
              ) : (
                <p className="bdw-whint">{tBoards('boards.wide.notify_off_hint')}</p>
              )}
            </section>
          )}

          {board.usePostReaders && (
            <section className={`bdw-wbox${isSecret ? '' : ' bdw-wbox--locked'}`}>
              <h3>{tBoards('boards.editor.readers')}</h3>
              {isSecret ? (
                <>
                  <p className="bdw-wsummary">
                    <Lock size={13} aria-hidden /> {readersSummary}
                  </p>
                  <Button variant="secondary"
                    onClick={() => setPicker('readers')}
                    iconLeft={<Users size={14} aria-hidden />}
                  >
                    {tBoards('boards.wide.pick_targets')}
                  </Button>
                </>
              ) : (
                <p className="bdw-whint">{tBoards('boards.wide.readers_locked')}</p>
              )}
            </section>
          )}
        </aside>
      </div>

      {picker === 'notify' && (
        <BoardTargetModal
          title={tBoards('boards.editor.notify_targets')}
          value={notifyTargets}
          onChange={setNotifyTargets}
          showNotify={false}
          onClose={() => setPicker(null)}
        />
      )}
      {picker === 'readers' && (
        <BoardTargetModal
          title={tBoards('boards.editor.readers')}
          value={readers}
          onChange={setReaders}
          onClose={() => setPicker(null)}
        />
      )}
    </section>
  );
}
